import { isMoneyMetric, type MetricValue, type Target } from '@taler/api-client'
import { formatMoney, formatRatioPercent, type Locale } from '@taler/domain'

export function formatMetricValueText(value: MetricValue, locale: Locale): string {
  return value.kind === 'money'
    ? formatMoney(value.value, { locale })
    : `${formatRatioPercent(value.value, { locale })}%`
}

/**
 * The bound or range a target defines, as one caption line: a band with both bounds, a
 * floor or a ceiling with one, an em dash with neither. Never assumes both bounds exist.
 */
export function targetLine(
  min: MetricValue | undefined,
  max: MetricValue | undefined,
  locale: Locale,
): string {
  if (min !== undefined && max !== undefined) {
    return `${formatMetricValueText(min, locale)} – ${formatMetricValueText(max, locale)}`
  }
  if (min !== undefined) return `≥ ${formatMetricValueText(min, locale)}`
  if (max !== undefined) return `≤ ${formatMetricValueText(max, locale)}`
  return '—'
}

export type StatusTone = 'ok' | 'danger' | 'neutral' | 'wait'

/** Five rendered states: `NOT_MET` splits on whether its own goal date has passed, and an unscoped account balance has no account to measure. */
export type StatusDisplay = 'MET' | 'NOT_MET_IN_PROGRESS' | 'NOT_MET_MISSED' | 'NO_DATA' | 'NO_ACCOUNT'

/** An account balance with no account chosen measures nothing: it is labelled, never a failed target. */
function isUnscopedAccountBalance(target: Target): boolean {
  return target.metric === 'ACCOUNT_BALANCE' && target.accountId === undefined
}

/**
 * `NO_DATA` is neither met nor missed, so it reads as an informational, neutral state
 * rather than a failure. A `NOT_MET` target only reads as a failure (`danger`) once its own
 * goal date has passed - with the date still ahead, or with no date at all, it is still in
 * progress, and reads as waiting (the attention tone with an hourglass), not as neutral.
 */
export function statusDisplay(target: Target): StatusDisplay {
  if (isUnscopedAccountBalance(target)) return 'NO_ACCOUNT'
  const { status, goalDatePassed } = target.evaluation
  if (status === 'MET') return 'MET'
  if (status === 'NO_DATA') return 'NO_DATA'
  return goalDatePassed ? 'NOT_MET_MISSED' : 'NOT_MET_IN_PROGRESS'
}

const TONE_BY_DISPLAY: Readonly<Record<StatusDisplay, StatusTone>> = {
  MET: 'ok',
  NOT_MET_MISSED: 'danger',
  NOT_MET_IN_PROGRESS: 'wait',
  NO_DATA: 'neutral',
  NO_ACCOUNT: 'neutral',
}

export function statusTone(display: StatusDisplay): StatusTone {
  return TONE_BY_DISPLAY[display]
}

/**
 * A money goal with only a positive lower bound reads as progress toward it; every other shape
 * reads as a range. A zero or negative floor has no progress to measure, so it stays a range.
 */
export function hasProgressTreatment(target: Target): boolean {
  const minimum = target.minValue
  return (
    isMoneyMetric(target.metric) &&
    minimum?.kind === 'money' &&
    minimum.value.units > 0n &&
    target.maxValue === undefined
  )
}
